import { useState, forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, KeyRound, Eye, EyeOff } from 'lucide-react';
import { api } from '@/api/client';

interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export function ChangePasswordPanel() {
  const [show, setShow] = useState(false);
  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>();
  const newPass = watch('newPassword');

  const save = useMutation({
    mutationFn: async (d: FormData) => (await api.post('/settings/change-password', {
      currentPassword: d.currentPassword,
      newPassword: d.newPassword,
    })).data,
    onSuccess: () => { toast.success('Password changed'); reset(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to change password'),
  });

  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4 max-w-md">
      <Field label="Current Password *" error={errors.currentPassword?.message}>
        <PasswordInput show={show} onToggle={() => setShow((s) => !s)}
          {...register('currentPassword', { required: 'Required' })} />
      </Field>

      <Field label="New Password *" error={errors.newPassword?.message}>
        <PasswordInput show={show} onToggle={() => setShow((s) => !s)}
          {...register('newPassword', { required: 'Required', minLength: { value: 8, message: 'Min 8 characters' } })} />
      </Field>

      <Field label="Confirm New Password *" error={errors.confirmPassword?.message}>
        <PasswordInput show={show} onToggle={() => setShow((s) => !s)}
          {...register('confirmPassword', {
            required: 'Required',
            validate: (v) => v === newPass || 'Passwords do not match',
          })} />
      </Field>

      <div className="text-xs text-slate-500">
        Use at least 8 characters. Mix upper-case, lower-case, numbers and a symbol for a stronger password.
      </div>

      <button type="submit" disabled={save.isPending}
        className="px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center gap-2 text-sm">
        {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
        Change Password
      </button>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}

type PasswordInputProps = React.InputHTMLAttributes<HTMLInputElement> & { show: boolean; onToggle: () => void };
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(({ show, onToggle, ...rest }, ref) => (
  <div className="relative">
    <input ref={ref} type={show ? 'text' : 'password'} {...rest} className="input pr-10" />
    <button type="button" onClick={onToggle}
      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
      {show ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  </div>
));
PasswordInput.displayName = 'PasswordInput';
